import { type ReactNode, useEffect, useState } from 'react';
import { planExpirationAlertStorage } from '../storage';
import { type AlertType, type PlanExpirationAlertProps } from '../types';
import { calculateDaysUntilExpiry, getExpirationAlertType } from '../utils/calculateDaysUntilExpiry';
import { PlanExpirationAlertBanner } from './PlanExpirationAlertBanner';

/**
 * 플랜 만료 알림 컨테이너 컴포넌트
 *
 * 플랜 만료 일수를 감시하고 D-7, D-3, D-day 시점에 배너 알림을 표시합니다.
 * - 중복 표시 방지: localStorage에 표시 여부 저장
 * - D-3 단계: 이전 D-7 알림 무효화
 * - 상태 관리: 로컬 state로 알림 표시/숨김 제어
 */
export const PlanExpirationAlertContainer = ({
  endDate,
  isDisabledPage,
  workspaceMemberType
}: PlanExpirationAlertProps): ReactNode => {
  const [shownAlertType, setShownAlertType] = useState<AlertType | null>(null);
  const daysUntilExpiry = calculateDaysUntilExpiry(endDate);
  useEffect(() => {
    if (!endDate || isDisabledPage || shownAlertType) {
      return;
    }

    // ============ 테스트용 주석 ============

    // 테스트 케이스 선택 (1개만 주석 해제):
    // const testEndDate = new Date();
    // // testEndDate.setDate(testEndDate.getDate() + 7); // D-7 테스트
    // // testEndDate.setDate(testEndDate.getDate() + 3); // D-3 테스트
    // testEndDate.setDate(testEndDate.getDate() + 0); // D-day 테스트
    // const endDateStr = testEndDate.toISOString().split('T')[0];
    // const daysUntilExpiry = calculateDaysUntilExpiry(endDateStr); // 위의 testEndDate 사용할 때 이것도 활성화

    const alertType = getExpirationAlertType(daysUntilExpiry);
    if (!alertType) {
      return;
    }

    // 이전 단계 알림 무효화 (중복 체크 전에 수행)
    if (alertType === 'D-3') {
      planExpirationAlertStorage.resetAlert('D-7');
    } else if (alertType === 'D-day') {
      planExpirationAlertStorage.resetAlert('D-3');
    }

    // 이미 해당 알림을 표시했으면 중복 표시 안함
    if (planExpirationAlertStorage.hasShown(alertType)) {
      return;
    }

    // 알림 표시 (저장은 X 버튼 클릭 시 수행)
    setShownAlertType(alertType);
  }, [daysUntilExpiry, endDate, isDisabledPage, shownAlertType]);
  const handleAlertClose = () => {
    if (!shownAlertType) return;
    // X 버튼 클릭 시에만 알림을 표시된 상태로 저장
    planExpirationAlertStorage.markAsShown(shownAlertType);
    setShownAlertType(null);
  };
  if (!shownAlertType) {
    return null;
  }
  return <PlanExpirationAlertBanner daysUntilExpiry={daysUntilExpiry} alertType={shownAlertType} onClose={handleAlertClose} workspaceMemberType={workspaceMemberType} data-sentry-element="PlanExpirationAlertBanner" data-sentry-component="PlanExpirationAlertContainer" data-sentry-source-file="PlanExpirationAlertContainer.tsx" />;
};